import { useQuery } from '@tanstack/react-query';
import { CreditCard, Layers, Users } from 'lucide-react';
import WidgetShell from '@/components/admin/dashboard/WidgetShell';
import StatCard from '@/components/admin/dashboard/StatCard';
import api from '@/lib/api';
import { formatCurrency } from '@/lib/utils';

/**
 * Subscription plans summary. GET /admin/subscription-plans returns every plan
 * (`subscription_plans.*`) plus a live `subscriber_count` (active
 * user_subscriptions per plan). We surface plan count + total active
 * subscribers and a short list of the busiest plans, linking to the plan
 * manager for the rest.
 */

interface PlanRow {
  id: string;
  name: string;
  price: number;
  interval: 'monthly' | 'quarterly' | 'yearly';
  is_active: boolean;
  tier_level: number;
  subscriber_count: number;
}

interface PlansResponse {
  plans: PlanRow[];
}

const intervalSuffix: Record<PlanRow['interval'], string> = {
  monthly: '/mo',
  quarterly: '/qtr',
  yearly: '/yr',
};

export default function SubscriptionsSummaryWidget() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard', 'subscriptions'],
    queryFn: () => api.get<PlansResponse>('/admin/subscription-plans'),
    staleTime: 60000,
  });

  const plans = data?.plans ?? [];
  const activePlans = plans.filter((p) => p.is_active).length;
  const totalSubscribers = plans.reduce((sum, p) => sum + (p.subscriber_count ?? 0), 0);

  const topPlans = [...plans]
    .sort((a, b) => b.subscriber_count - a.subscriber_count || a.tier_level - b.tier_level)
    .slice(0, 4);

  return (
    <WidgetShell
      title="Subscriptions"
      icon={<CreditCard />}
      to="/admin/subscription-plans"
      linkLabel="Manage plans"
      isLoading={isLoading}
      error={error}
      isEmpty={!!data && plans.length === 0}
      emptyMessage="No subscription plans yet."
    >
      {data && plans.length > 0 && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <StatCard
              title="Active plans"
              value={activePlans}
              icon={<Layers />}
              tone="purple"
              subtitle={`${plans.length} total`}
            />
            <StatCard
              title="Subscribers"
              value={totalSubscribers.toLocaleString()}
              icon={<Users />}
              tone="green"
            />
          </div>

          <ul className="divide-y divide-line">
            {topPlans.map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-3 py-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-ink">{p.name}</p>
                  <p className="text-xs text-stone-500">
                    {formatCurrency(p.price)}
                    {intervalSuffix[p.interval]}
                    {!p.is_active && ' · inactive'}
                  </p>
                </div>
                <span className="shrink-0 text-sm font-semibold text-ink">
                  {p.subscriber_count.toLocaleString()}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </WidgetShell>
  );
}
